import { Link } from "react-router-dom";
import { Kicker } from "@/components/ui/kicker";
import { features } from "@/lib/features";
import type { FindingCitation } from "./finding-detail-types";

interface FindingCitationsSectionProps {
  projectId: string;
  citations: FindingCitation[];
}

export function FindingCitationsSection({
  projectId,
  citations,
}: FindingCitationsSectionProps) {
  if (citations.length === 0) return null;

  return (
    <section className="space-y-4">
      <Kicker as="h2">Why it matters</Kicker>
      <p className="max-w-[70ch] text-base leading-relaxed">
        This finding is grounded in the project&apos;s domain knowledge.{" "}
        {citations.map((_cit, i) => (
          <sup
            key={i}
            className="text-xs font-semibold tabular-nums text-primary"
          >
            {i + 1}
          </sup>
        ))}
      </p>
      <ol className="list-none space-y-1.5 pl-0 text-sm text-muted-foreground">
        {citations.map((cit, i) => {
          const label = cit.characterization ?? cit.sourceId;
          const isSourceLink =
            features.knowledgeSources &&
            !!cit.sourceId &&
            !!projectId &&
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
              cit.sourceId,
            );
          return (
            <li key={i} className="flex gap-3">
              <sup className="shrink-0 text-xs font-semibold tabular-nums text-primary">
                {i + 1}
              </sup>
              <span>
                <span className="capitalize">
                  {cit.sourceType.replace(/_/g, " ")}
                </span>
                {" · "}
                {isSourceLink ? (
                  <Link
                    to={`/projects/${projectId}/sources/${cit.sourceId}`}
                    className="underline-offset-4 hover:text-foreground hover:underline"
                  >
                    {label}
                  </Link>
                ) : (
                  label
                )}
                {!cit.verified && <span className="ml-2">(unverified)</span>}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
